import { EventController } from "../eventhub/eventcontroller.js";
import { RidecellCallback } from "../models/ridecellcallback.js";
import {
  CacheEventData,
  EventType,
  EventName,
  RCEvent,
  CacheKeys,
  PublishEventData,
  NetworkEventData,
} from "../eventhub/eventmodels.js";
import { MemoryCache } from "../inmemorycache.js";
import { NetworkRequests } from "../networkrequestsbuilder.js";
import { Util } from "../util.js";

export class CustomerAuthenticationController {
  constructor() {
    /* Empty callback for use when we fire and forget event */
    this.emptyCallback = new RidecellCallback();
    this.emptyCallback.onSuccess = function success(result) {};
    this.emptyCallback.onFailure = function failure(error) {};
    this.saveAuthToken = this.saveAuthToken.bind(this);
    this.saveCustomerToCache = this.saveCustomerToCache.bind(this);
    this.getCustomer = this.getCustomer.bind(this);
  }
  /**
   * Creates an event to get authorization token from persistent cache.
   * @param {CallBack} callback - Callback for success and failure.
   */
  getAuthToken() {
    return new Promise((resolve, reject) => {
      const cacheEventData = new CacheEventData(
        EventType.READ_CACHE,
        CacheKeys.AUTH_TOKEN
      );
      const getAuthCallback = new RidecellCallback();
      getAuthCallback.onSuccess = (authToken) => {
        if (authToken) {
          MemoryCache.authorizationToken = authToken;
          resolve(authToken);
        } else {
          reject(Util.getErrorJson("No auth token found"));
        }
      };
      getAuthCallback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(
        new RCEvent(EventName.CACHE, cacheEventData, getAuthCallback)
      );
    });
  }

  /**
   * Creates an event to save auth token to persistent cache.
   * @param {string} authToken Authorization token to save.
   */
  saveAuthToken(authToken) {
    MemoryCache.authorizationToken = authToken;
    const cacheEventData = new CacheEventData(
      EventType.WRITE_CACHE,
      CacheKeys.AUTH_TOKEN,
      authToken
    );

    this.sendEvent(
      new RCEvent(EventName.CACHE, cacheEventData, this.emptyCallback)
    );

    const publishEventData = new PublishEventData(
      EventType.LOGIN_LOGOUT,
      authToken
    );
    this.sendEvent(
      new RCEvent(EventName.PUBLISH, publishEventData, new RidecellCallback())
    );
  }

  /**
   * Creates an event to refresh auth token from network.
   * Also saves auth token to persistent cache.
   * @param {string} authToken - Authorization token.
   * @param {CallBack} callback - Callback for success or failure.
   */
  reAuth(authToken) {
    return new Promise((resolve, reject) => {
      if (!authToken) {
        reject(Util.getErrorJson("Invalid auth token"));
        return;
      }

      const data = new FormData();
      data.append("token", authToken);

      const reAuthData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.renewToken,
        null, //queryData
        data, //formData
        false, //isPatch
        true //isMultipart
      );

      const reauthCallback = new RidecellCallback();
      reauthCallback.onSuccess = (result) => {
        this.saveAuthToken(result.auth_token);
        resolve(result);
      };
      reauthCallback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(
        new RCEvent(EventName.NETWORK, reAuthData, reauthCallback)
      );
    });
  }

  getCustomer() {
    return new Promise((resolve, reject) => {
      const customerData = new NetworkEventData(
        EventType.GET,
        NetworkRequests.customer
      );
      const saveToCache = this.saveCustomerToCache;

      const customerCallback = new RidecellCallback();
      customerCallback.onSuccess = (result) => {
        saveToCache(result);
        resolve(result);
      };
      customerCallback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(
        new RCEvent(EventName.NETWORK, customerData, customerCallback)
      );
    });
  }

  /**
   * Creates an event to save customer data to persistent cache.
   * Ignores success or failure in attempt.
   * @param {Object} customer
   */
  saveCustomerToCache(customer) {
    MemoryCache.customer = customer;
    const cacheEventData = new CacheEventData(
      EventType.WRITE_CACHE,
      CacheKeys.CUSTOMER,
      customer
    );

    this.sendEvent(
      new RCEvent(EventName.CACHE, cacheEventData, this.emptyCallback)
    );
  }

  /**
   * Creates a scheduled event to fetch customer data from network.
   */
  /*
    observeCustomer() {
        const CUSTOMER_OBSERVER_TIMEOUT_MILLISECONDS = 5000;

        const customerData = new NetworkEventData(
            EventType.GET,
            NetworkRequests.customer,
            null, //queryData
            null, //formData
            false, //isPatch
            false, //isMultipart
            true, //isGateway,
            true, //isScheduled
            CUSTOMER_OBSERVER_TIMEOUT_MILLISECONDS //frequency
        );

        const observeCallback = new RidecellCallback();
        observeCallback.onSuccess = function success(result) {
            this.saveCustomerToCache(result);
        }
        this.sendEvent(new RCEvent(EventName.NETWORK, customerData, observeCallback));
    }*/

  /**
   * Passes the event to event controller.
   * @param {RCEvent} event
   */
  sendEvent(event) {
    EventController.addEvent(event);
  }

  login({ email, password, deviceId }) {
    return new Promise((resolve, reject) => {
      if (!Util.isValidEmail(email)) {
        reject(Util.getErrorJson("Invalid email address"));
        return;
      }

      if (!password) {
        reject(Util.getErrorJson("Invalid password"));
        return;
      }

      const formdata = new FormData();
      const _saveToken = this.saveAuthToken;
      const _getCustomer = this.getCustomer;
      formdata.append("username", email);
      formdata.append("password", password);
      if (deviceId) formdata.append("device_id", deviceId);

      const eventData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.login,
        null, //queryData
        formdata, //formData
        false, //isPatch
        true //isMultipart
      );

      const loginCallback = new RidecellCallback();
      loginCallback.onSuccess = function success(result) {
        if (result["is_staff"] || result["nonce"]) {
          resolve(result);
        } else {
          const authToken = result.auth_token;
          _saveToken(authToken);
          _getCustomer()
            .then((customer) => {
              resolve(customer);
            })
            .catch((error) => {
              reject(error);
            });
        }
        //observeCustomer();
      };
      loginCallback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(new RCEvent(EventName.NETWORK, eventData, loginCallback));
    });
  }

  logout() {
    return new Promise((resolve, reject) => {
      MemoryCache.authorizationToken = null;
      MemoryCache.customer = null;

      let cacheEventData = new CacheEventData(
        EventType.CLEAR_CACHE,
        CacheKeys.AUTH_TOKEN
      );
      this.sendEvent(
        new RCEvent(EventName.CACHE, cacheEventData, this.emptyCallback)
      );
      cacheEventData = new CacheEventData(
        EventType.CLEAR_CACHE,
        CacheKeys.CUSTOMER
      );
      this.sendEvent(
        new RCEvent(EventName.CACHE, cacheEventData, this.emptyCallback)
      );

      resolve();
    });
  }

  mfaVerify({ code, nonce }) {
    return new Promise((resolve, reject) => {
      if (!code || !nonce) {
        reject(Util.getErrorJson("Must provide both code and nonce"));
        return;
      }
      const formData = new FormData();
      formData.append("code", code);
      formData.append("nonce", nonce);
      const eventData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.mfaVerify,
        null, //queryData
        formData, //formData
        false,
        true
      );

      const mfaVerifyCallback = new RidecellCallback();
      mfaVerifyCallback.onSuccess = (result) => resolve(result);
      mfaVerifyCallback.onFailure = (error) => reject(error);

      this.sendEvent(
        new RCEvent(EventName.NETWORK, eventData, mfaVerifyCallback)
      );
    });
  }

  mfaResend({ nonce }) {
    return new Promise((resolve, reject) => {
      if (!nonce) {
        reject(Util.getErrorJson("Must provide nonce"));
        return;
      }
      const formData = new FormData();
      formData.append("nonce", nonce);
      const eventData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.mfaResend,
        null, //queryData
        formData, //formData
        false,
        true
      );

      const mfaResendCallBack = new RidecellCallback();
      mfaResendCallBack.onSuccess = (result) => resolve(result);
      mfaResendCallBack.onFailure = (error) => reject(error);

      this.sendEvent(
        new RCEvent(EventName.NETWORK, eventData, mfaResendCallBack)
      );
    });
  }

  resetPasswordVerify({ uid, token }) {
    return new Promise((resolve, reject) => {
      if (!uid || !token) {
        reject(Util.getErrorJson("Must provide both uid and token"));
        return;
      }
      const formData = new FormData();
      formData.append("uid", uid);
      formData.append("token", token);
      const resetData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.resetPasswordVerify,
        null, //queryData
        formData, //formData
        false,
        true
      );

      const resetCallback = new RidecellCallback();
      resetCallback.onSuccess = (result) => resolve(result);
      resetCallback.onFailure = (error) => reject(error);

      this.sendEvent(new RCEvent(EventName.NETWORK, resetData, resetCallback));
    });
  }

  resetPasswordConfirm({ pass1, pass2, uid, token }) {
    return new Promise((resolve, reject) => {
      if (!uid || !token) {
        reject(Util.getErrorJson("Must provide both uid and token"));
        return;
      }
      const formData = new FormData();
      formData.append("uid", uid);
      formData.append("token", token);
      formData.append("new_password1", pass1);
      formData.append("new_password2", pass2);
      const resetData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.resetPasswordConfirm,
        null, //queryData
        formData, //formData
        false,
        true
      );

      const resetCallback = new RidecellCallback();
      resetCallback.onSuccess = (result) => resolve(result);
      resetCallback.onFailure = (error) => reject(error);

      this.sendEvent(new RCEvent(EventName.NETWORK, resetData, resetCallback));
    });
  }

  resetPassword({ customerContact, isLeasingCustomer }) {
    return new Promise((resolve, reject) => {
      if (!customerContact) {
        reject(Util.getErrorJson("Invalid phone number"));
        return;
      }

      const formData = new FormData();
      var settings = MemoryCache.settings;

      if (settings.passwordResetNotificationType == "sms")
        formData.append("phone_number", customerContact);
      else formData.append("email", customerContact);
      formData.append("is_leasing_customer", isLeasingCustomer);

      const resetData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.resetPassword,
        null, //queryData
        formData, //formData
        false,
        true
      );

      const resetCallback = new RidecellCallback();
      resetCallback.onSuccess = function (result) {
        resolve(result.message);
      };
      resetCallback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(new RCEvent(EventName.NETWORK, resetData, resetCallback));
    });
  }

  loggedInCustomer() {
    return MemoryCache.customer;
  }
}
